import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Download, Plus, X, Users, ArrowLeft, Copy, Check, Search, Mail, Send } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";

type Rep = {
    id: string;
    code: string;
    number?: number | null;
    name: string;
    notes: string;
    email?: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
    qr_url: string;
    scans?: number;
    requests?: number;
    conversion_rate?: number;
};

// -------------------------------------------------------------------------
// Vertreter-Liste
// -------------------------------------------------------------------------
export function AdminVertreterListPage() {
    const [items, setItems] = useState<Rep[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newNotes, setNewNotes] = useState("");
    const [q, setQ] = useState("");
    const [copiedCode, setCopiedCode] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api<{ items: Rep[] }>("/api/admin/representatives");
        if (res.ok && res.data) setItems(res.data.items || []);
        else setError(res.error || "Vertreter-Liste konnte nicht geladen werden.");
        setLoading(false);
    }, []);

    useEffect(() => { void load(); }, [load]);

    const createRep = async () => {
        setError("");
        setCreating(true);
        const res = await api<Rep>("/api/admin/representatives", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: newName, notes: newNotes, email: newEmail || null }),
        });
        setCreating(false);
        if (!res.ok) {
            setError(res.error || "Konnte Vertreter nicht anlegen.");
            return;
        }
        setNewName("");
        setNewEmail("");
        setNewNotes("");
        setShowCreate(false);
        await load();
    };

    const deactivate = async (r: Rep) => {
        if (!window.confirm(`Vertreter "${r.name}" deaktivieren? Historische Anfragen behalten ihre Zuordnung.`)) return;
        const res = await api(`/api/admin/representatives/${r.id}`, { method: "DELETE" });
        if (res.ok) await load();
        else setError(res.error || "Deaktivieren fehlgeschlagen.");
    };

    const reactivate = async (r: Rep) => {
        const res = await api(`/api/admin/representatives/${r.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ active: true }),
        });
        if (res.ok) await load();
    };

    const copyLink = async (url: string, key: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedCode(key);
            window.setTimeout(() => setCopiedCode(null), 1500);
        } catch {
            /* noop */
        }
    };

    const filtered = items.filter((r) =>
        !q.trim()
            ? true
            : (r.name.toLowerCase() + " " + r.code.toLowerCase() + " " + (r.number ?? "")).includes(q.toLowerCase()),
    );

    return (
        <div data-testid="admin-vertreter-list">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="hf-eyebrow">Vertriebspartner</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Vertreter</h1>
                    <p className="mt-2 text-hf-muted max-w-2xl">
                        Personalisierte QR-Codes für bis zu 50 Vertreter. Jede
                        Anfrage, die über einen Vertreter-Link eingeht, wird
                        automatisch zugeordnet.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="hf-btn hf-btn-primary"
                    data-testid="admin-vertreter-new"
                >
                    <Plus size={14} /> Neuer Vertreter
                </button>
            </div>

            {error && (
                <div
                    className="mt-6 p-3 rounded text-sm"
                    style={{
                        background: "hsl(var(--hf-danger) / 0.12)",
                        color: "hsl(var(--hf-danger))",
                        border: "1px solid hsl(var(--hf-danger) / 0.3)",
                    }}
                >
                    {error}
                </div>
            )}

            {/* Suche */}
            <div
                className="mt-6 flex items-center gap-2 px-3 py-2 rounded-sm"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <Search size={14} className="text-hf-muted" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Nach Name oder Code suchen…"
                    className="flex-1 bg-transparent outline-none text-sm"
                    data-testid="admin-vertreter-search"
                />
            </div>

            {loading ? (
                <div className="mt-8 text-hf-muted text-sm">Wird geladen…</div>
            ) : filtered.length === 0 ? (
                <div
                    className="mt-8 p-12 text-center rounded-sm"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px dashed hsl(var(--hf-line))" }}
                >
                    <Users className="mx-auto text-hf-muted mb-3" size={28} />
                    <div className="text-hf-muted">
                        {q ? "Keine Treffer für die Suche." : "Noch keine Vertreter angelegt."}
                    </div>
                </div>
            ) : (
                <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-vertreter-table">
                        <thead>
                            <tr style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                <th className="text-left py-3 text-hf-muted font-normal">Nr.</th>
                                <th className="text-left py-3 text-hf-muted font-normal">Name</th>
                                <th className="text-left py-3 text-hf-muted font-normal">Kurz-URL</th>
                                <th className="text-right py-3 text-hf-muted font-normal">Scans</th>
                                <th className="text-right py-3 text-hf-muted font-normal">Anfragen</th>
                                <th className="text-right py-3 text-hf-muted font-normal">Conversion</th>
                                <th className="text-right py-3 text-hf-muted font-normal">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr
                                    key={r.id}
                                    style={{ borderBottom: "1px solid hsl(var(--hf-line) / 0.5)",
                                             opacity: r.active ? 1 : 0.55 }}
                                    data-testid={`admin-vertreter-row-${r.code}`}
                                >
                                    <td className="py-3">
                                        <span
                                            className="inline-flex items-center justify-center min-w-[42px] px-2 py-1 rounded-sm font-display text-sm"
                                            style={{
                                                background: "hsl(var(--hf-gold) / 0.22)",
                                                color: "hsl(var(--hf-ink))",
                                                border: "1px solid hsl(var(--hf-gold) / 0.4)",
                                            }}
                                            data-testid={`admin-vertreter-number-${r.code}`}
                                        >
                                            {r.number != null ? r.number : "–"}
                                        </span>
                                    </td>
                                    <td className="py-3">
                                        <Link
                                            to={`/admin/vertreter/${r.id}`}
                                            className="text-hf-ink hover:text-hf-gold"
                                        >
                                            {r.name}
                                        </Link>
                                        {!r.active && (
                                            <span className="ml-2 text-xs text-hf-muted">(deaktiviert)</span>
                                        )}
                                    </td>
                                    <td className="py-3">
                                        <button
                                            type="button"
                                            onClick={() => copyLink(r.qr_url, r.code)}
                                            className="inline-flex items-center gap-1.5 font-mono text-xs text-hf-muted hover:text-hf-ink"
                                            title="Kurz-URL kopieren"
                                            data-testid={`admin-vertreter-copy-${r.code}`}
                                        >
                                            {r.qr_url.replace(/^https?:\/\//, "")}
                                            {copiedCode === r.code ? <Check size={12} /> : <Copy size={12} />}
                                        </button>
                                    </td>
                                    <td className="py-3 text-right">{r.scans ?? 0}</td>
                                    <td className="py-3 text-right">{r.requests ?? 0}</td>
                                    <td className="py-3 text-right">{((r.conversion_rate ?? 0) * 100).toFixed(1)}%</td>
                                    <td className="py-3 text-right">
                                        <div className="inline-flex gap-2">
                                            <a
                                                href={`${BACKEND_URL}/api/admin/representatives/${r.id}/qr.png`}
                                                className="hf-btn hf-btn-ghost text-xs"
                                                data-testid={`admin-vertreter-download-${r.code}`}
                                            >
                                                <Download size={12} /> QR
                                            </a>
                                            {r.active ? (
                                                <button
                                                    type="button"
                                                    onClick={() => void deactivate(r)}
                                                    className="hf-btn hf-btn-ghost text-xs"
                                                    data-testid={`admin-vertreter-deactivate-${r.code}`}
                                                >
                                                    Deaktivieren
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => void reactivate(r)}
                                                    className="hf-btn hf-btn-ghost text-xs"
                                                >
                                                    Aktivieren
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create-Modal */}
            {showCreate && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "hsl(var(--hf-ink) / 0.6)" }}
                    onClick={() => setShowCreate(false)}
                >
                    <div
                        className="max-w-md w-full p-6 rounded-sm"
                        style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="admin-vertreter-modal"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="hf-eyebrow">Neu anlegen</div>
                                <div className="font-display text-xl mt-1">Vertreter</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCreate(false)}
                                className="text-hf-muted hover:text-hf-ink"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="text-xs text-hf-muted uppercase tracking-wider">Name *</label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="z. B. Max Müller"
                                    className="mt-1 w-full px-3 py-2 rounded-sm bg-white outline-none text-sm"
                                    style={{ border: "1px solid hsl(var(--hf-line))" }}
                                    data-testid="admin-vertreter-input-name"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="text-xs text-hf-muted uppercase tracking-wider">E-Mail (für Wochenbericht)</label>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="z. B. max@beispiel.de"
                                    className="mt-1 w-full px-3 py-2 rounded-sm bg-white outline-none text-sm"
                                    style={{ border: "1px solid hsl(var(--hf-line))" }}
                                    data-testid="admin-vertreter-input-email"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-hf-muted uppercase tracking-wider">Notiz</label>
                                <textarea
                                    value={newNotes}
                                    onChange={(e) => setNewNotes(e.target.value)}
                                    placeholder="Optional — z. B. Region, Kontaktdaten…"
                                    rows={3}
                                    className="mt-1 w-full px-3 py-2 rounded-sm bg-white outline-none text-sm"
                                    style={{ border: "1px solid hsl(var(--hf-line))" }}
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowCreate(false)}
                                className="hf-btn hf-btn-ghost"
                            >Abbrechen</button>
                            <button
                                type="button"
                                onClick={() => void createRep()}
                                disabled={creating || newName.trim().length < 2}
                                className="hf-btn hf-btn-primary"
                                data-testid="admin-vertreter-submit"
                            >
                                {creating ? "Wird angelegt…" : "Anlegen"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// -------------------------------------------------------------------------
// Vertreter-Detail
// -------------------------------------------------------------------------
type RepDetail = Rep & {
    recent_requests?: Array<{
        id: string; name: string; email: string; location: string;
        object_type: string; message: string; created_at: string; status: string;
    }>;
    last_weekly_report?: {
        status: string;
        sent_at: string;
        to?: string | null;
        resend_id?: string | null;
        warning?: string | null;
        scans?: number;
        requests?: number;
    } | null;
};

export function AdminVertreterDetailPage() {
    const { id = "" } = useParams();
    const nav = useNavigate();
    const [rep, setRep] = useState<RepDetail | null>(null);
    const [error, setError] = useState("");
    const [editingEmail, setEditingEmail] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [sendingReport, setSendingReport] = useState(false);
    const [sendMsg, setSendMsg] = useState("");

    const loadDetail = useCallback(async () => {
        const res = await api<RepDetail>(`/api/admin/representatives/${id}/stats`);
        if (res.ok && res.data) {
            setRep(res.data);
            setEmailInput(res.data.email || "");
        } else setError(res.error || "Vertreter nicht gefunden.");
    }, [id]);

    useEffect(() => { void loadDetail(); }, [loadDetail]);

    const saveEmail = async () => {
        const trimmed = emailInput.trim();
        const res = await api<Rep>(`/api/admin/representatives/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: trimmed }),
        });
        if (!res.ok) {
            setError(res.error || "E-Mail konnte nicht gespeichert werden.");
            return;
        }
        setEditingEmail(false);
        setError("");
        await loadDetail();
    };

    const sendReportNow = async () => {
        if (!rep?.email) {
            setSendMsg("Zum Senden bitte zuerst eine E-Mail-Adresse hinterlegen.");
            return;
        }
        setSendingReport(true);
        setSendMsg("");
        const res = await api<{ ok: boolean; log: { warning?: string; resend_id?: string } }>(
            `/api/admin/representatives/${id}/send-weekly-report`,
            { method: "POST" },
        );
        setSendingReport(false);
        if (res.ok && res.data?.ok) {
            setSendMsg(`Wochenbericht gesendet (Resend-ID: ${res.data.log?.resend_id || "?"}).`);
            await loadDetail();
        } else {
            setSendMsg(res.data?.log?.warning || res.error || "Send fehlgeschlagen.");
        }
    };

    if (error) {
        return (
            <div>
                <button onClick={() => nav("/admin/vertreter")} className="hf-btn hf-btn-ghost">
                    <ArrowLeft size={14} /> Zurück
                </button>
                <div className="mt-6 text-hf-danger">{error}</div>
            </div>
        );
    }
    if (!rep) return <div className="text-hf-muted">Wird geladen…</div>;

    return (
        <div data-testid="admin-vertreter-detail">
            <button onClick={() => nav("/admin/vertreter")} className="hf-btn hf-btn-ghost">
                <ArrowLeft size={14} /> Zurück zur Übersicht
            </button>

            <div className="mt-4 hf-eyebrow">Vertreter {rep.number != null ? `· Nr. ${rep.number}` : ""}</div>
            <h1 className="font-display text-3xl md:text-4xl mt-2">
                {rep.number != null && (
                    <span
                        className="inline-flex items-center justify-center align-middle mr-3 min-w-[52px] h-[52px] px-3 rounded-sm font-display text-2xl"
                        style={{
                            background: "hsl(var(--hf-gold) / 0.25)",
                            color: "hsl(var(--hf-ink))",
                            border: "1px solid hsl(var(--hf-gold) / 0.45)",
                        }}
                        data-testid="admin-vertreter-detail-number"
                    >
                        {rep.number}
                    </span>
                )}
                {rep.name}
            </h1>
            {rep.notes && <p className="mt-2 text-hf-muted max-w-2xl whitespace-pre-line">{rep.notes}</p>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                <MiniStat label="Kurz-URL" value={rep.qr_url.replace(/^https?:\/\//, "")} mono />
                <MiniStat label="Scans (30T)" value={String(rep.scans ?? 0)} />
                <Link
                    to={`/admin/vertreter/${rep.id}/anfragen`}
                    className="block group"
                    data-testid="admin-vertreter-anfragen-link"
                >
                    <div
                        className="p-4 rounded-sm h-full transition-colors"
                        style={{
                            background: "hsl(var(--hf-gold-soft) / 0.55)",
                            border: "1px solid hsl(var(--hf-gold) / 0.4)",
                        }}
                    >
                        <div className="hf-eyebrow flex items-center justify-between" style={{ fontSize: 10 }}>
                            <span>Anfragen</span>
                            <span className="text-[10px] text-hf-muted group-hover:text-hf-ink">Details →</span>
                        </div>
                        <div className="mt-2 font-display text-2xl text-hf-ink">{String(rep.requests ?? 0)}</div>
                    </div>
                </Link>
                <MiniStat label="Conversion" value={`${((rep.conversion_rate ?? 0) * 100).toFixed(1)}%`} />
            </div>

            <div
                className="mt-8 p-6 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                style={{ background: "hsl(var(--hf-gold-soft) / 0.4)", border: "1px solid hsl(var(--hf-gold) / 0.4)" }}
            >
                <div>
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>QR-Code für Print & Digital</div>
                    <div className="mt-1 text-sm font-mono text-hf-ink break-all">{rep.qr_url}</div>
                </div>
                <a
                    href={`${BACKEND_URL}/api/admin/representatives/${rep.id}/qr.png`}
                    className="hf-btn hf-btn-primary shrink-0"
                    data-testid="admin-vertreter-download-qr"
                >
                    <Download size={14} /> QR herunterladen
                </a>
            </div>

            {/* Wochenbericht-Sektion */}
            <div
                className="mt-8 p-6 rounded-sm"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                data-testid="admin-vertreter-weekly"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="hf-eyebrow flex items-center gap-2"><Mail size={12} /> Wochenübersicht</div>
                        <div className="font-display text-xl mt-1">Automatischer Report montags 9 Uhr</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => void sendReportNow()}
                        disabled={!rep.email || sendingReport}
                        className="hf-btn hf-btn-primary shrink-0"
                        data-testid="admin-vertreter-send-weekly"
                    >
                        <Send size={14} /> {sendingReport ? "Wird gesendet…" : "Jetzt neu senden"}
                    </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="text-xs text-hf-muted uppercase tracking-wider">E-Mail-Adresse</div>
                        {editingEmail ? (
                            <div className="mt-1 flex gap-2">
                                <input
                                    type="email"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    placeholder="vertreter@beispiel.de"
                                    className="flex-1 px-3 py-2 rounded-sm bg-white outline-none text-sm"
                                    style={{ border: "1px solid hsl(var(--hf-line))" }}
                                    data-testid="admin-vertreter-email-input"
                                    autoFocus
                                />
                                <button type="button" onClick={() => void saveEmail()} className="hf-btn hf-btn-primary text-sm">Speichern</button>
                                <button type="button" onClick={() => { setEditingEmail(false); setEmailInput(rep.email || ""); }} className="hf-btn hf-btn-ghost text-sm">Abbrechen</button>
                            </div>
                        ) : (
                            <div className="mt-1 flex items-center gap-2">
                                <div className="text-sm text-hf-ink font-mono">
                                    {rep.email || <span className="text-hf-muted">— nicht gesetzt —</span>}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setEditingEmail(true)}
                                    className="hf-btn hf-btn-ghost text-xs"
                                    data-testid="admin-vertreter-email-edit"
                                >
                                    {rep.email ? "Ändern" : "Hinzufügen"}
                                </button>
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-xs text-hf-muted uppercase tracking-wider">Letzte Wochenübersicht</div>
                        {rep.last_weekly_report ? (
                            <div className="mt-1 text-sm">
                                <div className="text-hf-ink">
                                    {fmtDateTime(rep.last_weekly_report.sent_at, { dateStyle: "medium", timeStyle: "short" })}
                                    <span
                                        className="ml-2 text-xs px-2 py-0.5 rounded-sm"
                                        style={{
                                            background: rep.last_weekly_report.status === "sent"
                                                ? "hsl(var(--hf-gold) / 0.25)"
                                                : "hsl(var(--hf-danger) / 0.15)",
                                            color: "hsl(var(--hf-ink))",
                                        }}
                                    >
                                        {rep.last_weekly_report.status === "sent" ? "gesendet" : "fehlgeschlagen"}
                                    </span>
                                </div>
                                {rep.last_weekly_report.status === "sent" && (
                                    <div className="text-xs text-hf-muted mt-1">
                                        Scans/Anfragen der Woche: {rep.last_weekly_report.scans ?? 0} / {rep.last_weekly_report.requests ?? 0}
                                        {rep.last_weekly_report.resend_id && (
                                            <span> · Resend-ID: {rep.last_weekly_report.resend_id.slice(0, 8)}…</span>
                                        )}
                                    </div>
                                )}
                                {rep.last_weekly_report.warning && (
                                    <div className="text-xs text-hf-danger mt-1">{rep.last_weekly_report.warning}</div>
                                )}
                            </div>
                        ) : (
                            <div className="mt-1 text-sm text-hf-muted">Noch nicht versendet.</div>
                        )}
                    </div>
                </div>
                {sendMsg && (
                    <div className="mt-4 text-xs text-hf-muted" data-testid="admin-vertreter-send-msg">{sendMsg}</div>
                )}
            </div>

            <div
                className="mt-8 p-6 rounded-sm"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <div className="hf-eyebrow">Letzte Anfragen</div>
                <div className="font-display text-xl mt-1">Über {rep.name}</div>
                {(!rep.recent_requests || rep.recent_requests.length === 0) ? (
                    <div className="mt-4 text-hf-muted text-sm">
                        Noch keine Anfragen über diesen Vertreter.
                    </div>
                ) : (
                    <table className="w-full text-sm mt-4" data-testid="admin-vertreter-requests-table">
                        <thead>
                            <tr style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                <th className="text-left py-2 text-hf-muted font-normal">Datum</th>
                                <th className="text-left py-2 text-hf-muted font-normal">Name</th>
                                <th className="text-left py-2 text-hf-muted font-normal">Objekt</th>
                                <th className="text-left py-2 text-hf-muted font-normal">Ort</th>
                                <th className="text-left py-2 text-hf-muted font-normal">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rep.recent_requests.map((c) => (
                                <tr key={c.id} style={{ borderBottom: "1px solid hsl(var(--hf-line) / 0.5)" }}>
                                    <td className="py-2 text-xs text-hf-muted">
                                        {fmtDateTime(c.created_at, { day: "2-digit", month: "2-digit", year: "numeric" })}
                                    </td>
                                    <td className="py-2">
                                        <Link to={`/admin/anfragen/${c.id}`} className="text-hf-ink hover:text-hf-gold">
                                            {c.name}
                                        </Link>
                                    </td>
                                    <td className="py-2 text-hf-muted">{c.object_type || "–"}</td>
                                    <td className="py-2 text-hf-muted">{c.location || "–"}</td>
                                    <td className="py-2">
                                        <span className="text-xs px-2 py-0.5 rounded-sm"
                                              style={{ background: "hsl(var(--hf-surface))",
                                                       color: "hsl(var(--hf-ink))" }}>
                                            {c.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function MiniStat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
    return (
        <div
            className="p-4 rounded-sm"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
        >
            <div className="hf-eyebrow" style={{ fontSize: 10 }}>{label}</div>
            <div className={`mt-2 font-display text-2xl text-hf-ink ${mono ? "font-mono text-lg" : ""}`}>{value}</div>
        </div>
    );
}
